/**
 * Autosave: what a failure says, and what a failure announces (F-JR-23).
 *
 * The *mechanism* — the 650 ms debounce, the single coalescing writer, the
 * strictly increasing `savedAt` — lives in `controller.ts`, because it is
 * inseparable from the working entry it saves. What lives here is the part that
 * has to be identical no matter who calls it: the mapping from a repository
 * error to the sentence the user reads, and the announcement built from it.
 *
 * Three error codes have their own sentence because they mean different things
 * to the person reading them, and everything else gets the default — which is
 * deliberately vague about *which* step failed rather than guessing. Every one
 * of them ends by saying the writing is still there, because that is the
 * property the fail-closed write path actually guarantees: nothing was
 * replaced, and the text is still on screen.
 */

import type { JournalRepositoryErrorCode } from "@ff/domain";
import { JOURNAL_AUTOSAVE_COPY } from "@ff/domain";

export function journalAutosaveFailureMessage(
  error: JournalRepositoryErrorCode | undefined,
): string {
  switch (error) {
    case "revisionConflict":
      return JOURNAL_AUTOSAVE_COPY.revisionConflict;
    case "recoveryRequired":
      return JOURNAL_AUTOSAVE_COPY.recoveryRequired;
    // Both attachment integrity failures say the same thing on purpose: the
    // user's next action is the same either way, and "corrupt" versus "missing"
    // is a distinction only the repository can act on.
    case "attachmentMissing":
    case "attachmentCorrupt":
      return JOURNAL_AUTOSAVE_COPY.attachmentUnverified;
    default:
      return JOURNAL_AUTOSAVE_COPY.defaultFailure;
  }
}

/**
 * §8.5: "Journal autosave failures announce **Journal not saved. {message}**".
 *
 * The prefix and the message are joined here rather than at the call site so
 * that the one sentence assistive technology hears cannot drift between the
 * banner, the status line and the announcement.
 */
export function journalAutosaveAnnouncement(message: string): string {
  return `${JOURNAL_AUTOSAVE_COPY.announcementPrefix} ${message}`;
}
